/**
 * Main-thread entry point for bounded `.xlsx` import parsing (AUD-007).
 *
 * `parseWorkbookBounded` checks the file size before reading any bytes, then
 * hands the buffer to a dedicated module worker (`xlsxImport.worker.ts`)
 * with a hard wall-clock timeout. The worker is terminated on every path —
 * success, error and timeout — so a hostile workbook can never keep CPU
 * after its deadline.
 */
import {
  DEFAULT_WORKBOOK_LIMITS,
  WorkbookParseError,
  parseWorkbookBuffer,
  type CellValue,
  type ParseRequest,
  type ParseResponse,
  type WorkbookLimits,
} from './xlsxWorkerProtocol';

export {
  DEFAULT_WORKBOOK_LIMITS,
  WorkbookParseError,
  type CellValue,
  type WorkbookLimits,
} from './xlsxWorkerProtocol';

/** Typed error code → user-facing message (pt-BR), for the import dialogs. */
export const WORKBOOK_ERROR_MESSAGES: Record<string, string> = {
  FILE_TOO_LARGE: 'Arquivo excede o limite de 5 MB',
  ROW_LIMIT_EXCEEDED: 'Planilha excede o limite de 5.000 linhas',
  COLUMN_LIMIT_EXCEEDED: 'Planilha excede o limite de 100 colunas',
  CELL_LIMIT_EXCEEDED: 'Planilha excede o limite de 100.000 células preenchidas',
  EMPTY_WORKBOOK: 'Planilha vazia ou ilegível',
  PARSE_TIMEOUT: 'Tempo limite de leitura excedido (10s)',
  PARSE_ERROR: 'Planilha vazia ou ilegível',
};

/** Map any error thrown by `parseWorkbookBounded` to a display message. */
export function workbookErrorMessage(err: unknown): string {
  if (err instanceof WorkbookParseError) {
    return WORKBOOK_ERROR_MESSAGES[err.code] ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Erro desconhecido';
}

/** `File.arrayBuffer()` with a FileReader fallback (jsdom has no arrayBuffer). */
function readFileBytes(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new WorkbookParseError('PARSE_ERROR', 'Could not read file'));
    reader.readAsArrayBuffer(file);
  });
}

function parseInWorker(buffer: ArrayBuffer, limits: WorkbookLimits): Promise<CellValue[][]> {
  return new Promise<CellValue[][]>((resolve, reject) => {
    const worker = new Worker(new URL('./xlsxImport.worker.ts', import.meta.url), {
      type: 'module',
    });
    const finish = (fn: () => void): void => {
      clearTimeout(timer);
      worker.terminate();
      fn();
    };
    const timer = setTimeout(() => {
      finish(() => reject(new WorkbookParseError('PARSE_TIMEOUT')));
    }, limits.timeoutMs);
    worker.onmessage = (event: MessageEvent<ParseResponse>) => {
      const data = event.data;
      finish(() => {
        if (data.ok) resolve(data.rows);
        else reject(new WorkbookParseError(data.code, data.message));
      });
    };
    worker.onerror = () => {
      finish(() => reject(new WorkbookParseError('PARSE_ERROR', 'Worker failed to parse file')));
    };
    const request: ParseRequest = { buffer, limits };
    worker.postMessage(request, [buffer]);
  });
}

function parseInline(buffer: ArrayBuffer, limits: WorkbookLimits): Promise<CellValue[][]> {
  // ponytail: no Worker in jsdom/vitest — same bounded parser, Promise.race
  // for the timeout (cannot hard-kill an inline parse; browsers use the worker).
  return Promise.race([
    parseWorkbookBuffer(buffer, limits),
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new WorkbookParseError('PARSE_TIMEOUT')), limits.timeoutMs);
    }),
  ]);
}

/**
 * Parse an `.xlsx` {@link File} into primitive cell rows under hard limits.
 *
 * @throws WorkbookParseError with a typed `code` on any violation.
 */
export async function parseWorkbookBounded(
  file: File,
  limits: WorkbookLimits = DEFAULT_WORKBOOK_LIMITS,
): Promise<CellValue[][]> {
  if (file.size > limits.maxBytes) {
    throw new WorkbookParseError(
      'FILE_TOO_LARGE',
      `File is ${file.size} bytes (limit ${limits.maxBytes})`,
    );
  }
  const buffer = await readFileBytes(file);
  return typeof Worker === 'undefined'
    ? parseInline(buffer, limits)
    : parseInWorker(buffer, limits);
}

/**
 * Convert primitive cell rows (first row = headers) into the row-object
 * shape `parseProductRows` consumes (SheetJS `sheet_to_json` equivalent,
 * `defval: ''`). Columns with a blank header and fully-empty rows are
 * dropped.
 */
export function rowsToObjects(rows: CellValue[][]): Array<Record<string, unknown>> {
  const [headerRow, ...dataRows] = rows;
  if (!headerRow) return [];
  const headers = headerRow.map((h) => String(h ?? '').trim());
  const objects: Array<Record<string, unknown>> = [];
  for (const row of dataRows) {
    const obj: Record<string, unknown> = {};
    let hasValue = false;
    headers.forEach((header, i) => {
      if (!header) return;
      const value = row[i] ?? '';
      obj[header] = value;
      if (value !== '' && value !== null) hasValue = true;
    });
    if (hasValue) objects.push(obj);
  }
  return objects;
}
